/**
 * LinkedIn Account Discovery Service (Story 10.1, Task 3 — rebuilt by Story 31.21)
 *
 * Discovers LinkedIn Organization Pages and Ad Accounts.
 *
 * Story 31.21 (Epic 31, ADR-0004 catalog contract):
 * - Migrated off the sunset unversioned `/v2` finders onto the versioned
 *   `/rest` surface (`organizationAcls`, `adAccounts?q=search`) with the
 *   mandatory `LinkedIn-Version` AND `X-Restli-Protocol-Version: 2.0.0` headers.
 * - Split into per-family functions so each adapter hits ONLY its endpoints:
 *   `discoverLinkedInOrganizations()` (linkedin-pages) and
 *   `discoverLinkedInAdAccounts()` (linkedin-ads). ONE OAuth grant → TWO
 *   per-platform catalogs with zero cross-family requests.
 * - STRICT error semantics in the per-family functions: any HTTP/network
 *   failure on a membership call (first page OR mid-pagination), a cursor
 *   loop, or hitting the page cap with more pages remaining returns
 *   `{ ok: false, error }` — a truncated catalog is NEVER returned as
 *   success. Best-effort degradation survives ONLY for per-org NAME
 *   enrichment (synthetic fallback, membership never dropped).
 * - Defensive dedupe by id in each family (Map, first occurrence wins) —
 *   the DB UNIQUE index is the backstop, not the first line.
 * - `state=APPROVED` filter on the org ACL finder (REQUESTED/REVOKED roles
 *   never enter the catalog).
 *
 * The combined `discoverLinkedInAccounts()` wrapper keeps its EXACT legacy
 * result shape `{ organizations, adAccounts, partial }` with LENIENT
 * per-family degradation (family failure → empty list + `partial: true`)
 * because `apps/server/src/routes/integrations/linkedin-oauth.ts` consumes
 * it directly and is out of scope for 31.21.
 *
 * Pagination styles differ per family (do NOT copy one loop to the other):
 * - Organization ACLs: INDEX pagination (`start`/`count`, follow
 *   `paging.links` rel="next" or advance until a short page).
 * - Ad accounts: CURSOR pagination (`pageSize`/`pageToken` via
 *   `metadata.nextPageToken`, cursor pagination since version 202401).
 */

import type { AdapterError, AdapterErrorCode } from "../adapter";
import { safeFetch } from "../safe-fetch";
import { LINKEDIN_API_VERSION } from "./scopes";

// ============================================================================
// Constants
// ============================================================================

/** Versioned LinkedIn REST surface (Story 31.21 — was the sunset /v2 base) */
const LINKEDIN_API_BASE = "https://api.linkedin.com/rest";
const PER_CALL_TIMEOUT_MS = 5_000;
const PAGE_SIZE = 100;
const MAX_PAGES = 50;

// ============================================================================
// Types
// ============================================================================

export interface DiscoveredOrganization {
  id: string;
  name: string;
}

export interface DiscoveredAdAccount {
  id: string;
  name: string;
  /** Ad account status (e.g. ACTIVE, CANCELED, REMOVED) — catalog membership is NOT filtered by status */
  status?: string;
  /** ISO currency code for the account */
  currency?: string;
  /** Associated organization URN (e.g. urn:li:organization:123) */
  reference?: string;
  /** True for LinkedIn test ad accounts */
  test?: boolean;
}

export interface LinkedInDiscoveryResult {
  organizations: DiscoveredOrganization[];
  adAccounts: DiscoveredAdAccount[];
  /** True if discovery was cut short (timeout/abort) or a family failed in the lenient wrapper */
  partial: boolean;
}

export interface LinkedInDiscoveryOptions {
  accessToken: string;
  /** AbortController signal for timeout budget */
  signal?: AbortSignal;
  /** Base URL override for testing */
  baseUrl?: string;
}

/**
 * Options for the strict per-family discovery functions (Story 31.21).
 * `tenantId`/`integrationId` are used ONLY for structured partial-discovery
 * warn logs (skipped URN rows, name-enrichment fallbacks).
 */
export interface LinkedInFamilyDiscoveryOptions extends LinkedInDiscoveryOptions {
  tenantId?: string;
  integrationId?: string;
}

/**
 * Strict per-family discovery result (Story 31.21 AC #4).
 * `ok: false` carries a full AdapterError (safeFetch taxonomy passthrough —
 * 401→token_expired, 403→permission_denied, 429→rate_limited,
 * 5xx/network→retryable provider_unavailable/network_error) so adapters can
 * map it through their existing `parseLinkedInError` fallback chain.
 */
export type LinkedInOrganizationsDiscoveryResult =
  | { ok: true; organizations: DiscoveredOrganization[]; skipped: number }
  | { ok: false; error: AdapterError };

export type LinkedInAdAccountsDiscoveryResult =
  | { ok: true; adAccounts: DiscoveredAdAccount[]; skipped: number }
  | { ok: false; error: AdapterError };

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds standard LinkedIn API headers.
 * All LinkedIn API calls MUST include the LinkedIn-Version header;
 * the versioned /rest finders also require X-Restli-Protocol-Version: 2.0.0.
 */
function linkedInHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    "LinkedIn-Version": LINKEDIN_API_VERSION,
    "X-Restli-Protocol-Version": "2.0.0",
  };
}

/**
 * Parses a LinkedIn organization URN to extract the numeric ID.
 * Format: urn:li:organization:{id}
 *
 * Intentionally org-only: `urn:li:organizationBrand:{id}` rows that can
 * appear in ACLs are excluded from the catalog by design (returns null).
 *
 * @returns The numeric ID string, or null if parsing fails
 */
export function parseOrganizationUrn(urn: string): string | null {
  const match = urn.match(/^urn:li:organization:(\d+)$/);
  return match?.[1] ?? null;
}

/**
 * Defensive HTTP-status mapping for mocked/alternate transports.
 * Real safeFetch only returns success when response.ok, so this is a
 * safety net mirroring its taxonomy.
 */
function mapHttpStatusToError(status: number, context: string): AdapterError {
  let code: AdapterErrorCode;
  if (status === 401) code = "token_expired";
  else if (status === 403) code = "permission_denied";
  else if (status === 429) code = "rate_limited";
  else if (status >= 500) code = "provider_unavailable";
  else code = "invalid_response";

  return {
    code,
    message: `LinkedIn ${context} request failed with HTTP ${status}`,
    retryable: code === "rate_limited" || code === "provider_unavailable",
    providerStatus: status,
  };
}

/** Structured partial-discovery warn log (tenantId/integrationId when available). */
function warnPartialDiscovery(
  event: string,
  opts: LinkedInFamilyDiscoveryOptions,
  details: Record<string, unknown>
): void {
  console.warn(
    JSON.stringify({
      event,
      tenantId: opts.tenantId,
      integrationId: opts.integrationId,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}

// ============================================================================
// Organization Discovery (Story 31.21 Task 1.2 — linkedin-pages family)
// ============================================================================

interface OrgAclsResponse {
  elements?: Array<{
    /** Versioned field name (li-lms-2026-04); older examples show `organization` */
    organizationTarget?: string;
    organization?: string;
  }>;
  paging?: {
    start?: number;
    count?: number;
    total?: number;
    links?: Array<{ rel?: string; href?: string }>;
  };
}

interface OrgResponse {
  id: number;
  localizedName?: string;
  vanityName?: string;
}

/**
 * Discovers LinkedIn Organization Pages the authenticated member administers
 * (linkedin-pages family ONLY — zero ad-account endpoint requests).
 *
 * Endpoint: GET {base}/organizationAcls?q=roleAssignee&role=ADMINISTRATOR&state=APPROVED
 * Pagination: INDEX-based (`start`/`count`), following `paging.links`
 * rel="next" or advancing `start += count` until a short page.
 *
 * STRICT semantics: any membership-call failure (first page or
 * mid-pagination), abort, or page-cap-with-more-remaining returns
 * `{ ok: false, error }`. Name lookup via GET {base}/organizations/{id} is
 * best-effort ONLY — failure falls back to `Organization {id}` and NEVER
 * drops membership.
 */
export async function discoverLinkedInOrganizations(
  options: LinkedInFamilyDiscoveryOptions
): Promise<LinkedInOrganizationsDiscoveryResult> {
  const { accessToken, signal, baseUrl = LINKEDIN_API_BASE } = options;
  const headers = linkedInHeaders(accessToken);

  // Step 1: Fetch organization ACL membership (strict, paginate to completion)
  const orgIds = new Map<string, true>(); // defensive dedupe — first occurrence wins
  let skipped = 0;
  let start = 0;
  let pages = 0;

  for (;;) {
    if (signal?.aborted) {
      return {
        ok: false,
        error: {
          code: "network_error",
          message: "LinkedIn organization discovery aborted before completing the catalog",
          retryable: true,
        },
      };
    }

    const url = `${baseUrl}/organizationAcls?q=roleAssignee&role=ADMINISTRATOR&state=APPROVED&count=${PAGE_SIZE}&start=${start}`;
    const result = await safeFetch(url, { headers, signal }, PER_CALL_TIMEOUT_MS);

    if (!result.success) {
      // safeFetch taxonomy passthrough (401/403/429/5xx/network) — never a silent empty catalog
      return { ok: false, error: result.error };
    }

    const response = result.data;
    if (!response.ok) {
      return { ok: false, error: mapHttpStatusToError(response.status, "organization ACL") };
    }

    let data: OrgAclsResponse;
    try {
      data = (await response.json()) as OrgAclsResponse;
    } catch {
      return {
        ok: false,
        error: {
          code: "invalid_response",
          message: "Failed to parse LinkedIn organization ACL response as JSON",
          retryable: false,
        },
      };
    }

    const elements = data.elements ?? [];
    for (const element of elements) {
      const urn = element.organizationTarget ?? element.organization;
      const orgId = urn ? parseOrganizationUrn(urn) : null;
      if (orgId) {
        if (!orgIds.has(orgId)) orgIds.set(orgId, true);
      } else {
        skipped++;
      }
    }

    pages++;

    // LinkedIn clamps `count` server-side on some REST finders (docs' own
    // examples use count=10) — trust the EFFECTIVE page size echoed in
    // paging.count, never the requested PAGE_SIZE, for both full-page
    // detection and index advancement. Otherwise a clamped response either
    // silently truncates (short-vs-100 break) or skips records (start += 100
    // over N-element pages).
    const pagingCount = data.paging?.count;
    const effectiveCount =
      typeof pagingCount === "number" && pagingCount > 0 ? pagingCount : PAGE_SIZE;
    const nextLinkHref = data.paging?.links?.find((l) => l.rel === "next")?.href;
    const fullPage = elements.length > 0 && elements.length >= effectiveCount;
    if (!nextLinkHref && !fullPage) break; // short page without a next link → catalog complete

    if (pages >= MAX_PAGES) {
      // Hitting the cap with more pages remaining must NOT masquerade as a complete catalog
      return {
        ok: false,
        error: {
          code: "invalid_response",
          message: `LinkedIn organization ACL pagination exceeded ${MAX_PAGES} pages with more pages remaining — refusing to return a truncated catalog`,
          retryable: false,
        },
      };
    }

    // Canonical index advance: prefer the server's own rel="next" pointer,
    // fall back to start + effective page size.
    let nextStart = start + effectiveCount;
    if (nextLinkHref) {
      const match = nextLinkHref.match(/[?&]start=(\d+)/);
      if (match) nextStart = Number(match[1]);
    }
    if (nextStart <= start) {
      // Non-advancing start index — the index-pagination twin of a cursor loop
      return {
        ok: false,
        error: {
          code: "invalid_response",
          message:
            "LinkedIn organization ACL pagination did not advance (repeated start index) — refusing to return a truncated catalog",
          retryable: false,
        },
      };
    }
    start = nextStart;
  }

  if (skipped > 0) {
    warnPartialDiscovery("linkedin_discovery_org_urns_skipped", options, { skipped });
  }

  // Step 2: Best-effort name enrichment — failure NEVER drops membership
  const organizations: DiscoveredOrganization[] = [];
  for (const orgId of orgIds.keys()) {
    if (signal?.aborted) {
      // Out of budget: keep membership with synthetic names, no more network calls
      organizations.push({ id: orgId, name: `Organization ${orgId}` });
      continue;
    }

    const url = `${baseUrl}/organizations/${orgId}`;
    const result = await safeFetch(url, { headers, signal }, PER_CALL_TIMEOUT_MS);

    if (!result.success || !result.data.ok) {
      warnPartialDiscovery("linkedin_discovery_org_name_fallback", options, { orgId });
      organizations.push({ id: orgId, name: `Organization ${orgId}` });
      continue;
    }

    let orgData: OrgResponse | undefined;
    try {
      orgData = (await result.data.json()) as OrgResponse;
    } catch {
      warnPartialDiscovery("linkedin_discovery_org_name_fallback", options, { orgId });
    }

    organizations.push({
      id: orgId,
      name: orgData?.localizedName ?? orgData?.vanityName ?? `Organization ${orgId}`,
    });
  }

  return { ok: true, organizations, skipped };
}

// ============================================================================
// Ad Account Discovery (Story 31.21 Task 1.3 — linkedin-ads family)
// ============================================================================

interface AdAccountsSearchResponse {
  elements?: Array<{
    id?: number | string;
    name?: string;
    status?: string;
    currency?: string;
    reference?: string;
    test?: boolean;
  }>;
  metadata?: {
    nextPageToken?: string;
  };
}

/**
 * Discovers LinkedIn Ad Accounts the authenticated member can access
 * (linkedin-ads family ONLY — zero organization endpoint requests).
 *
 * Endpoint: GET {base}/adAccounts?q=search — query omitted returns ALL
 * accounts the caller can access; `id`/`name`/`status`/`currency`/`reference`
 * come back inline (the legacy `adAccountUsersV2` two-step + per-account N+1
 * detail fetch are gone).
 * Pagination: CURSOR-based (`pageSize`/`pageToken` via `metadata.nextPageToken`).
 *
 * STRICT semantics: any call failure, abort, cursor loop (repeated
 * pageToken), or page-cap-with-more-remaining returns `{ ok: false, error }`.
 * Membership is NOT filtered by status — CANCELED/REMOVED/test accounts stay
 * in the catalog with their status in metadata (the picker UI decides).
 */
export async function discoverLinkedInAdAccounts(
  options: LinkedInFamilyDiscoveryOptions
): Promise<LinkedInAdAccountsDiscoveryResult> {
  const { accessToken, signal, baseUrl = LINKEDIN_API_BASE } = options;
  const headers = linkedInHeaders(accessToken);

  const accountsById = new Map<string, DiscoveredAdAccount>(); // defensive dedupe — first occurrence wins
  let skipped = 0;
  let nextPageToken: string | undefined;
  const seenTokens = new Set<string>();
  let pages = 0;

  for (;;) {
    if (signal?.aborted) {
      return {
        ok: false,
        error: {
          code: "network_error",
          message: "LinkedIn ad account discovery aborted before completing the catalog",
          retryable: true,
        },
      };
    }

    let url = `${baseUrl}/adAccounts?q=search&pageSize=${PAGE_SIZE}`;
    if (nextPageToken) {
      url += `&pageToken=${encodeURIComponent(nextPageToken)}`;
    }

    const result = await safeFetch(url, { headers, signal }, PER_CALL_TIMEOUT_MS);

    if (!result.success) {
      return { ok: false, error: result.error };
    }

    const response = result.data;
    if (!response.ok) {
      return { ok: false, error: mapHttpStatusToError(response.status, "ad account search") };
    }

    let data: AdAccountsSearchResponse;
    try {
      data = (await response.json()) as AdAccountsSearchResponse;
    } catch {
      return {
        ok: false,
        error: {
          code: "invalid_response",
          message: "Failed to parse LinkedIn ad account search response as JSON",
          retryable: false,
        },
      };
    }

    for (const element of data.elements ?? []) {
      if (element.id === undefined || element.id === null) {
        skipped++;
        continue;
      }
      const id = String(element.id);
      if (accountsById.has(id)) continue;
      const account: DiscoveredAdAccount = {
        id,
        name: element.name ?? `Ad Account ${id}`,
      };
      if (element.status !== undefined) account.status = element.status;
      if (element.currency !== undefined) account.currency = element.currency;
      if (element.reference !== undefined) account.reference = element.reference;
      if (element.test !== undefined) account.test = element.test;
      accountsById.set(id, account);
    }

    pages++;

    const token = data.metadata?.nextPageToken;
    if (!token) break; // no cursor → catalog complete

    if (seenTokens.has(token)) {
      // Cursor loop — would paginate forever; refuse to return a poisoned catalog
      return {
        ok: false,
        error: {
          code: "invalid_response",
          message: "LinkedIn ad account pagination returned a repeated pageToken (cursor loop) — refusing to return a truncated catalog",
          retryable: false,
        },
      };
    }
    seenTokens.add(token);

    if (pages >= MAX_PAGES) {
      return {
        ok: false,
        error: {
          code: "invalid_response",
          message: `LinkedIn ad account pagination exceeded ${MAX_PAGES} pages with more pages remaining — refusing to return a truncated catalog`,
          retryable: false,
        },
      };
    }

    nextPageToken = token;
  }

  if (skipped > 0) {
    warnPartialDiscovery("linkedin_discovery_ad_accounts_skipped", options, { skipped });
  }

  return { ok: true, adAccounts: [...accountsById.values()], skipped };
}

// ============================================================================
// Combined Discovery Wrapper (legacy shape — consumed by apps/server linkedin-oauth)
// ============================================================================

/**
 * Discovers LinkedIn accounts after OAuth flow completes.
 *
 * LENIENT wrapper over the two strict per-family functions, preserving the
 * legacy `{ organizations, adAccounts, partial }` shape consumed directly by
 * `apps/server/src/routes/integrations/linkedin-oauth.ts`. A family failure
 * degrades to an empty list for that family and flips `partial: true` —
 * adapters needing strict `adapterErr` semantics call the per-family
 * functions instead (Story 31.21 AC #2/#4).
 */
export async function discoverLinkedInAccounts(
  options: LinkedInDiscoveryOptions
): Promise<LinkedInDiscoveryResult> {
  const { accessToken, signal, baseUrl = LINKEDIN_API_BASE } = options;

  // Families are independent — fetch in parallel
  const [orgResult, adResult] = await Promise.all([
    discoverLinkedInOrganizations({ accessToken, signal, baseUrl }),
    discoverLinkedInAdAccounts({ accessToken, signal, baseUrl }),
  ]);

  let partial = false;
  let organizations: DiscoveredOrganization[] = [];
  let adAccounts: DiscoveredAdAccount[] = [];

  if (orgResult.ok) {
    organizations = orgResult.organizations;
  } else {
    partial = true;
    console.warn(
      JSON.stringify({
        event: "linkedin_discovery_org_family_failed",
        code: orgResult.error.code,
        message: orgResult.error.message,
        timestamp: new Date().toISOString(),
      })
    );
  }

  if (adResult.ok) {
    adAccounts = adResult.adAccounts;
  } else {
    partial = true;
    console.warn(
      JSON.stringify({
        event: "linkedin_discovery_ads_family_failed",
        code: adResult.error.code,
        message: adResult.error.message,
        timestamp: new Date().toISOString(),
      })
    );
  }

  if (signal?.aborted) {
    partial = true;
  }

  return {
    organizations,
    adAccounts,
    partial,
  };
}
